// Shared UI building blocks. Styling lives in styles/components.css.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import QRCode from "qrcode";
import { Icon, type IconName } from "./Icon";
import { t } from "../i18n/sl";
import { ApiError } from "../api";

// ------------------------------------------------------------------ chips

export interface ChipOption<V extends string = string> {
  value: V;
  label: string;
  hint?: string;
  /** an AI suggestion the person has not picked: drawn with the AI mark */
  mark?: boolean;
}

/** A group of toggle chips. Single choice (radio semantics) or multi (checkbox semantics). */
export function ChipGroup<V extends string>({
  options,
  value,
  onChange,
  multi = false,
  label,
  suggested,
  size = "md",
  disabled,
  className,
}: {
  options: ChipOption<V>[];
  value: V | V[] | null | undefined;
  onChange: (v: V | V[]) => void;
  multi?: boolean;
  label?: string;
  /** values pre-filled by AI and not yet confirmed: drawn dashed */
  suggested?: boolean;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  className?: string;
}) {
  const selected = new Set<string>(Array.isArray(value) ? value : value ? [value] : []);
  const toggle = (v: V) => {
    if (multi) {
      const next = new Set(selected);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      onChange(options.map((o) => o.value).filter((x) => next.has(x)));
    } else {
      onChange(v);
    }
  };
  return (
    <div
      className={`chips chips--${size} ${suggested ? "chips--suggested" : ""} ${className ?? ""}`}
      role="group"
      aria-label={label}
    >
      {options.map((o) => {
        const on = selected.has(o.value);
        return (
          <button
            key={o.value}
            type="button"
            className={`chip ${on ? "is-on" : ""} ${o.mark && !on ? "chip--mark" : ""}`}
            aria-pressed={on}
            disabled={disabled}
            onClick={() => toggle(o.value)}
            title={o.hint}
          >
            {on && <Icon name="check" size={size === "lg" ? 18 : 15} strokeWidth={2.2} />}
            {o.mark && !on && <Icon name="nodes" size={13} />}
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ AI marks

/** "Predlog AI" tag. The node mark is the Agentcija logo idea: an agent proposed this. */
export function AiTag({ label = t.common.aiSuggestion, title = t.common.aiSuggestionHint }: { label?: string; title?: string }) {
  return (
    <span className="ai-tag" title={title}>
      <Icon name="nodes" size={13} />
      {label}
    </span>
  );
}

/**
 * Shows what the AI is doing while a slow call runs (5-30 s). Steps advance on a timer
 * and the last one stays active until the call returns. Calm, no spinner wheel.
 */
export function AiWorking({
  steps,
  interval = 3200,
  note,
  compact = false,
}: {
  steps: string[];
  interval?: number;
  note?: string;
  compact?: boolean;
}) {
  const [i, setI] = useState(0);
  useEffect(() => {
    setI(0);
    const id = window.setInterval(() => setI((x) => Math.min(x + 1, steps.length - 1)), interval);
    return () => clearInterval(id);
  }, [steps.length, interval]);
  return (
    <div className={`ai-working ${compact ? "ai-working--compact" : ""}`} role="status" aria-live="polite">
      <ol className="ai-steps">
        {steps.map((s, idx) => {
          const state = idx < i ? "done" : idx === i ? "now" : "todo";
          return (
            <li key={s} className={`ai-step ai-step--${state}`} aria-current={state === "now" ? "step" : undefined}>
              <span className="ai-step-mark" aria-hidden="true">
                {state === "done" ? <Icon name="check" size={13} strokeWidth={2.4} /> : null}
              </span>
              <span>{s}</span>
              {state === "now" && <span className="ai-dots" aria-hidden="true"><i /><i /><i /></span>}
            </li>
          );
        })}
      </ol>
      {note && <p className="ai-working-note">{note}</p>}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="spinner" role="status">
      <span className="ai-dots" aria-hidden="true"><i /><i /><i /></span>
      {label && <span className="sr-only">{label}</span>}
    </span>
  );
}

export function PageLoading() {
  return (
    <div className="page-loading">
      <Spinner label={t.common.loading} />
    </div>
  );
}

// ------------------------------------------------------------------ states

export function EmptyState({
  title,
  text,
  action,
  icon = "nodes",
}: {
  title: string;
  text?: string;
  action?: ReactNode;
  icon?: IconName;
}) {
  return (
    <div className="empty">
      <span className="empty-icon" aria-hidden="true">
        <Icon name={icon} size={26} />
      </span>
      <h3 className="empty-title">{title}</h3>
      {text && <p className="empty-text">{text}</p>}
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message || t.common.errorGeneric;
  if (e instanceof TypeError) return t.common.networkError;
  if (e instanceof Error && e.message) return e.message;
  return t.common.errorGeneric;
}

export function ErrorBox({ error, onRetry, title = t.common.errorTitle }: { error: unknown; onRetry?: () => void; title?: string }) {
  return (
    <div className="error-box" role="alert">
      <Icon name="alert" size={20} />
      <div className="stack" style={{ ["--gap" as string]: "4px" }}>
        <strong>{title}</strong>
        <span className="soft">{errorMessage(error)}</span>
      </div>
      {onRetry && (
        <button className="btn btn-secondary btn-sm" onClick={onRetry}>
          {t.common.retry}
        </button>
      )}
    </div>
  );
}

export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p className="inline-error" role="alert">
      <Icon name="alert" size={16} />
      {errorMessage(error)}
    </p>
  );
}

// ------------------------------------------------------------------ fields

export function Field({
  label,
  hint,
  children,
  htmlFor,
  optional,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  htmlFor?: string;
  optional?: boolean;
}) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={htmlFor}>
        {label}
        {optional && <span className="field-optional"> ({t.common.optional})</span>}
      </label>
      {hint && <p className="field-hint">{hint}</p>}
      {children}
    </div>
  );
}

/** Text area that grows with its content. */
export function AutoTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { minRows?: number }) {
  const { minRows = 3, className, ...rest } = props;
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [props.value]);
  return <textarea ref={ref} rows={minRows} className={`input textarea ${className ?? ""}`} {...rest} />;
}

/** Editable list of short strings shown as removable chips. */
export function TagInput({
  value,
  onChange,
  placeholder = t.card.addItemPlaceholder,
  label,
  suggested,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  label: string;
  /** values pre-filled by AI and not yet confirmed: drawn dashed */
  suggested?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft("");
  };
  return (
    <div className={`tag-input ${suggested && value.length ? "is-suggested" : ""}`}>
      {value.map((v) => (
        <span key={v} className="tag">
          {v}
          <button type="button" className="tag-x" aria-label={`${t.common.remove}: ${v}`} onClick={() => onChange(value.filter((x) => x !== v))}>
            <Icon name="x" size={12} strokeWidth={2.2} />
          </button>
        </span>
      ))}
      <input
        className="tag-input-field"
        value={draft}
        aria-label={label}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add();
          } else if (e.key === "Backspace" && !draft && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={add}
      />
    </div>
  );
}

// ------------------------------------------------------------------ copy + QR

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  }
}

export function CopyField({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const id = useId();
  return (
    <div className="copy-field">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input id={id} className="input copy-input" readOnly value={value} onFocus={(e) => e.currentTarget.select()} />
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={async () => {
          if (await copyText(value)) {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
          }
        }}
      >
        <Icon name={copied ? "check" : "copy"} size={16} />
        <span aria-live="polite">{copied ? t.common.copied : t.common.copy}</span>
      </button>
    </div>
  );
}

export function QrCode({ value, size = 168, label }: { value: string; size?: number; label: string }) {
  const [svg, setSvg] = useState<string>("");
  useEffect(() => {
    let alive = true;
    QRCode.toString(value, {
      type: "svg",
      margin: 1,
      errorCorrectionLevel: "M",
      color: { dark: "#14130f", light: "#ffffff" },
    }).then((s) => alive && setSvg(s));
    return () => {
      alive = false;
    };
  }, [value]);
  return (
    <div
      className="qr"
      style={{ width: size, height: size }}
      role="img"
      aria-label={label}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

// ------------------------------------------------------------------ overlays

/** Open dialogs, newest last: keys go only to the top one (a confirm dialog over the drawer). */
const trapStack: object[] = [];

function useFocusTrap(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const me = {};
    trapStack.push(me);
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const focusables = () =>
      Array.from(
        el?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((x) => x.offsetParent !== null);
    window.setTimeout(() => {
      const first = el?.querySelector<HTMLElement>("[data-autofocus]") ?? focusables()[0];
      first?.focus();
    }, 30);
    const onKey = (e: KeyboardEvent) => {
      if (trapStack[trapStack.length - 1] !== me) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      } else if (e.key === "Tab") {
        const f = focusables();
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      trapStack.splice(trapStack.indexOf(me), 1);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  return ref;
}

export function Modal({
  open,
  onClose,
  title,
  sub,
  children,
  footer,
  width = 640,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  sub?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const ref = useFocusTrap(open, onClose);
  const id = useId();
  if (!open) return null;
  return createPortal(
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className="modal" role="dialog" aria-modal="true" aria-labelledby={id} style={{ maxWidth: width }}>
        <header className="modal-head">
          <div className="stack" style={{ ["--gap" as string]: "6px" }}>
            <h2 id={id} className="modal-title">
              {title}
            </h2>
            {sub && <p className="soft">{sub}</p>}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label={t.common.close}>
            <Icon name="x" size={20} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({
  open,
  onClose,
  label,
  children,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  children: ReactNode;
}) {
  const ref = useFocusTrap(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="overlay overlay--drawer" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className="drawer" role="dialog" aria-modal="true" aria-label={label}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// ------------------------------------------------------------------ toasts

export interface ToastOptions {
  /** one button in the toast, e.g. "Razveljavi" or "Odpri" */
  action?: { label: string; onClick: () => void };
  /** ms; defaults: 3.5 s, 6 s for errors, 8 s with an action */
  duration?: number;
}
/** ok: done; error: failed; info: calm news that needs no action (e.g. newer data was loaded) */
export type ToastKind = "ok" | "error" | "info";
interface ToastItem {
  id: number;
  text: string;
  kind: ToastKind;
  action?: ToastOptions["action"];
}
type ToastFn = (text: string, kind?: ToastKind, opts?: ToastOptions) => void;
const ToastCtx = createContext<ToastFn>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const remove = useCallback((id: number) => setItems((x) => x.filter((i) => i.id !== id)), []);
  const push = useCallback<ToastFn>(
    (text, kind = "ok", opts) => {
      const id = Date.now() + Math.random();
      setItems((x) => [...x.slice(-2), { id, text, kind, action: opts?.action }]);
      const ms = opts?.duration ?? (opts?.action ? 8000 : kind === "ok" ? 3500 : 6500);
      window.setTimeout(() => remove(id), ms);
    },
    [remove],
  );
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((i) => (
          <div key={i.id} className={`toast toast--${i.kind} ${i.action ? "has-action" : ""}`}>
            <Icon name={i.kind === "ok" ? "check" : i.kind === "info" ? "refresh" : "alert"} size={16} strokeWidth={2.2} />
            <span>{i.text}</span>
            {i.action && (
              <button
                type="button"
                className="toast-action"
                onClick={() => {
                  i.action!.onClick();
                  remove(i.id);
                }}
              >
                {i.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return useContext(ToastCtx);
}

// ------------------------------------------------------------------ long AI jobs

/**
 * Calm progress for a long AI job (30-60 s) with an honest time estimate. Steps advance with the
 * expected duration; the bar fills toward it and never claims "done" before the server says so.
 * `startedAt` (epoch ms) keeps the elapsed time right when the person leaves and comes back.
 */
export function JobProgress({
  steps,
  expectedSec,
  startedAt,
  note,
  children,
}: {
  steps: string[];
  expectedSec: number;
  startedAt?: number;
  note?: string;
  children?: ReactNode;
}) {
  const [start] = useState(() => startedAt ?? Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = Math.max(0, Math.round((now - (startedAt ?? start)) / 1000));
  const frac = elapsed / Math.max(1, expectedSec);
  // ease out toward 95 %; the last bit belongs to the server's answer
  const pct = Math.min(95, 100 * (1 - Math.exp(-2.2 * frac)));
  const stepIdx = Math.min(steps.length - 1, Math.floor(frac * steps.length));
  return (
    <div className="job" role="status" aria-live="polite">
      <ol className="ai-steps">
        {steps.map((s, idx) => {
          const state = idx < stepIdx ? "done" : idx === stepIdx ? "now" : "todo";
          return (
            <li key={s} className={`ai-step ai-step--${state}`} aria-current={state === "now" ? "step" : undefined}>
              <span className="ai-step-mark" aria-hidden="true">
                {state === "done" ? <Icon name="check" size={13} strokeWidth={2.4} /> : null}
              </span>
              <span>{s}</span>
              {state === "now" && (
                <span className="ai-dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
              )}
            </li>
          );
        })}
      </ol>
      <div className="job-meter" aria-hidden="true">
        <span className="job-meter-fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="job-time">
        <span className="num">{t.jobs.elapsed(elapsed)}</span>
        <span className="mute"> - {elapsed > expectedSec * 1.3 ? t.jobs.overtime : t.jobs.usual(expectedSec)}</span>
      </p>
      {note && <p className="job-note">{note}</p>}
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ misc

export function Logo({ height = 26, light = false }: { height?: number; light?: boolean }) {
  return (
    <img
      src={light ? "/agentcija-logo-light.svg" : "/agentcija-logo-dark.svg"}
      alt="Agentcija"
      height={height}
      style={{ height, width: (height * 651) / 163 }}
    />
  );
}

export function ProgressBar({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = Math.max(0, Math.min(100, (value / Math.max(1, max)) * 100));
  return (
    <div className="progress" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value}>
      <span className="progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function SectionHead({ title, sub, action }: { title: string; sub?: ReactNode; action?: ReactNode }) {
  return (
    <div className="section-head">
      <div className="stack" style={{ ["--gap" as string]: "6px" }}>
        <h2 className="section-title">{title}</h2>
        {sub && <p className="section-sub">{sub}</p>}
      </div>
      {action && <div className="section-action">{action}</div>}
    </div>
  );
}

export function PageHead({ title, sub, actions }: { title: string; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div className="stack" style={{ ["--gap" as string]: "8px" }}>
        <h1 className="page-title">{title}</h1>
        {sub && <p className="page-sub">{sub}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}
